import { Spinner } from '@/components/glitch-ui/spinner';
import { useState, useMemo, forwardRef, useImperativeHandle } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAtomValue } from 'jotai';
import { ColumnDef } from '@tanstack/react-table';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Trash2, AlertCircle, ShieldOff } from 'lucide-react';
import { useToast } from '@/hooks/useToast';
import { createIpExclusions, deleteIpExclusion } from '@/lib/apis';
import { accountIpExclusionsQueryOptions, allCampaignIpExclusionsQueryOptions } from '@/lib/query-options';
import type { IpExclusion } from '@/lib/types';
import { NoGoogleAdsAccountSelected } from '@/components/glitch-ui/no-google-ads-account';
import { DataTable } from '@/components/glitch-ui/data-table';
import { IconButton } from '@/components/glitch-ui/icon-button';
import { ConformationModal } from '@/components/glitch-ui/confirmation-modal';
import { FieldError } from '@/components/glitch-ui/field-error';
import { googleAdsAccountAtom } from '@/store';
import { getCampaignShortName } from '@/lib/utils';
import { CampaignPill } from '@/components/glitch-ui/campaign-pill';
import type { AssetFilter } from './account-sitelinks';

// The campaign's distinguishing part; the business name repeats on every row
// and is what truncation would keep. Same rule as sitelinks and callouts.
const campaignSourceLabel = (name: string): string => {
  const parts = name.split(' | ');
  return parts.length >= 3 ? parts.slice(1).join(' | ') : name;
};

// An address this account blocks, whether blocked on the account or on
// campaigns (#6745). Account and campaign IP exclusions are fully isolated
// Google Ads resources, so the same address can appear as both.
interface IpExclusionRow {
  key: string;
  ip_address: string;
  /** Set for an account exclusion: the record this row can delete. */
  account: IpExclusion | null;
  /** The campaigns blocking this address. Empty for an account exclusion. */
  campaigns: { id: number; slug: string; name: string }[];
}

function parseIpAddresses(raw: string): string[] {
  return raw
    .split(/[\n,]+/)
    .map((ip) => ip.trim())
    .filter((ip) => ip.length > 0);
}

export interface AccountIpExclusionsRef {
  openAdd: () => void;
}

export const AccountIpExclusions = forwardRef<AccountIpExclusionsRef, { filter?: AssetFilter }>(
  ({ filter = {} }, ref) => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const googleAdsAccount = useAtomValue(googleAdsAccountAtom);
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [bulkIps, setBulkIps] = useState('');
  // Tell-on-click (#5857): Add stays enabled; a click with no valid IPs marks the field.
  const [ipsError, setIpsError] = useState('');
  const [exclusionToDelete, setExclusionToDelete] = useState<IpExclusion | null>(null);

  const {
    data: exclusionsData,
    isLoading,
    isError,
  } = useQuery(accountIpExclusionsQueryOptions(googleAdsAccount?.googleads_account_id));

  // The campaign half of the same list (#6745).
  const { data: campaignExclusionsData } = useQuery(
    allCampaignIpExclusionsQueryOptions(googleAdsAccount?.googleads_account_id),
  );

  const createMutation = useMutation({
    mutationFn: (ips: string[]) => createIpExclusions(ips),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['account-ip-exclusions'] });
      handleCloseAddDialog();
      toast({ title: 'IP exclusions added', description: 'The IP addresses have been blocked from seeing ads.' });
    },
    onError: (error: Error) => {
      toast({ title: 'Failed to add IP exclusions', description: error.message || 'Please try again later.', variant: 'destructive' });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (resourceName: string) => deleteIpExclusion(resourceName),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['account-ip-exclusions'] });
      setExclusionToDelete(null);
    },
    onError: (error: Error) => {
      toast({ title: 'Failed to remove IP exclusion', description: error.message || 'Please try again later.', variant: 'destructive' });
    },
  });

  const handleAdd = () => {
    const ips = parseIpAddresses(bulkIps);
    // Tell-on-click (#5857): mark the field instead of a silently disabled button.
    if (ips.length === 0) {
      setIpsError(bulkIps.trim() ? 'No valid IP addresses found' : 'Required');
      return;
    }
    createMutation.mutate(ips);
  };

  const handleCloseAddDialog = () => {
    setIsAddDialogOpen(false);
    setBulkIps('');
    setIpsError('');
  };

  const handleConfirmDelete = () => {
    if (exclusionToDelete) deleteMutation.mutate(exclusionToDelete.resource_name);
  };

  const rows = useMemo((): IpExclusionRow[] => {
    const accountRows: IpExclusionRow[] = (exclusionsData?.exclusions ?? []).map((e) => ({
      key: `account-${e.resource_name}`,
      ip_address: e.ip_address,
      account: e,
      campaigns: [],
    }));
    const campaignRows: IpExclusionRow[] = (campaignExclusionsData?.exclusions ?? []).map((e) => ({
      key: `campaign-${e.ip_address}`,
      ip_address: e.ip_address,
      account: null,
      campaigns: e.campaigns.map((c) => ({ id: c.id, slug: c.slug, name: getCampaignShortName(c) })),
    }));
    return [...accountRows, ...campaignRows];
  }, [exclusionsData, campaignExclusionsData]);

  // The toolbar's filter decides what shows: where the address is blocked.
  const sourcePicked = filter.source ?? [];
  const visibleRows = useMemo(() => {
    if (sourcePicked.length === 0) return rows;
    return rows.filter((row) =>
      row.account ? sourcePicked.includes('account') : row.campaigns.some((c) => sourcePicked.includes(String(c.id))),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, sourcePicked.join('|')]);

  const exclusionCount = visibleRows.length;

  const columns = useMemo((): ColumnDef<IpExclusionRow>[] => [
    {
      accessorKey: 'ip_address',
      header: `IP Exclusions (${exclusionCount})`,
      meta: { className: 'px-6 w-[75%] whitespace-normal align-top' },
      cell: ({ row }) => (
        <span className="text-sm font-mono font-medium text-gray-900">{row.original.ip_address}</span>
      ),
    },
    {
      id: 'source',
      header: 'Source',
      enableSorting: false,
      meta: { className: 'px-6 w-[15%] whitespace-normal align-top' },
      // Where the address is blocked: the account, so it applies to every
      // campaign, or the campaigns that block it.
      cell: ({ row }) =>
        row.original.campaigns.length === 0 ? (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-50 text-blue-600">
            Account
          </span>
        ) : (
          <div className="flex flex-col items-start gap-1">
            {row.original.campaigns.map((c) => (
              <CampaignPill
                key={c.id}
                slug={c.slug}
                name={campaignSourceLabel(c.name)}
                title={c.name}
                hash="settings"
                className="max-w-[200px]"
              />
            ))}
          </div>
        ),
    },
    {
      id: 'actions',
      enableSorting: false,
      // A campaign's exclusion is removed on that campaign, so this row only
      // points at it.
      cell: ({ row }) =>
        row.original.account ? (
          <div className="flex justify-end">
            <IconButton
              variant="destructive"
              aria-label="Delete IP exclusion"
              onClick={() => setExclusionToDelete(row.original.account)}
            >
              <Trash2 className="h-4 w-4" aria-hidden="true" />
            </IconButton>
          </div>
        ) : null,
      meta: { className: 'px-6 w-[10%] text-right align-top' },
    },
  ], [exclusionCount]);

  useImperativeHandle(ref, () => ({ openAdd: () => setIsAddDialogOpen(true) }), []);

  if (!googleAdsAccount?.googleads_account_id) {
    return (
      <NoGoogleAdsAccountSelected description="A Google Ads account needs to be selected for this organization before IP exclusions can be managed." />
    );
  }

  const exclusions = visibleRows;

  if (isError) {
    return (
      <div className="bg-white shadow-sm border border-red-200 rounded-lg overflow-hidden">
        <div className="p-6 flex items-center gap-3 text-red-600">
          <AlertCircle className="h-5 w-5" />
          <span className="text-sm">Failed to load IP exclusions</span>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="border rounded-lg bg-white overflow-hidden">
        <div className="flex items-center justify-center gap-3 py-16">
          <Spinner size="md" className="text-gray-500" />
          <span className="text-gray-500">Loading IP exclusions...</span>
        </div>
      </div>
    );
  }

  return (
    <div>
      <DataTable
        data={exclusions}
        columns={columns}
        variant="flush"
        pageSize={999}
        getRowId={(row) => row.key}
        emptyIcon={ShieldOff}
        emptyTitle={sourcePicked.length > 0 ? 'No IP Exclusions Match' : 'No IP Exclusions'}
        emptyDescription={
          sourcePicked.length > 0
            ? 'No address on this account or any of its campaigns matches the filter.'
            : 'Block IP addresses to prevent specific users from seeing your ads across all campaigns.'
        }
      />

      <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
        <DialogContent className="sm:max-w-[500px]" aria-describedby={undefined}>
          <DialogHeader>
            <DialogTitle>Add IP Exclusions</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>IP Addresses</Label>
                <FieldError message={ipsError} />
              </div>
              <Textarea
                value={bulkIps}
                onChange={(e) => { setBulkIps(e.target.value); setIpsError(''); }}
                placeholder={'Enter IPs, one per line or comma-separated\n192.168.1.1\n198.51.100.0/24, 203.0.113.5'}
                className="min-h-[150px] text-gray-900 font-mono"
                error={!!ipsError}
                autoFocus
              />
              <p className="text-xs text-gray-500">
                Individual IPv4/IPv6 addresses are supported, along with /24 and /32 IPv4 masks (e.g. 198.51.100.0/24).
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={handleCloseAddDialog} disabled={createMutation.isPending}>
              Cancel
            </Button>
            <Button
              type="button"
              onClick={handleAdd}
              disabled={createMutation.isPending}
              className="bg-[#290BD9] hover:bg-[#2309C5] text-white"
            >
              {createMutation.isPending ? 'Saving...' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConformationModal
        isOpen={!!exclusionToDelete}
        onClose={() => setExclusionToDelete(null)}
        onConfirm={handleConfirmDelete}
        title="Remove IP Exclusion"
        description={`Are you sure you want to unblock "${exclusionToDelete?.ip_address}"? Ads will become visible to this IP again.`}
        confirmText="Remove"
        cancelText="Cancel"
        isLoading={deleteMutation.isPending}
      />
      </div>
    );
  },
);
AccountIpExclusions.displayName = 'AccountIpExclusions';
